import { AGENT_FIELDS, REQUIRED_AGENT_FIELD_NAMES, STANDARD_CATEGORIES, type FieldDefinition } from './schema';
import type { DirectoryFilter, DirectoryNode, KnowledgeRecord, OperationRecord, OverviewData, RawRecord } from './types';

const COMMON_GROUPS = new Set(['平台化专项', '跨平台', '矩阵级共性', '流程规范']);
const PLATFORM_ORDER = ['F平台', 'G平台', 'D平台', 'E平台', 'H平台', '平台化与共性', '跨车型资料', '待整理'];
const CATEGORY_ORDER = new Map(STANDARD_CATEGORIES.map((category, index) => [category.replace(/^分类-\d+_/, ''), index]));

export function textValue(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (Array.isArray(value)) {
    const first = value[0];
    if (typeof first === 'string') return first.trim() || null;
    if (first && typeof first === 'object' && 'text' in first && typeof first.text === 'string') return first.text.trim() || null;
  }
  if (value && typeof value === 'object') {
    if ('text' in value && typeof value.text === 'string') return value.text.trim() || null;
    if ('link' in value && typeof value.link === 'string') return value.link.trim() || null;
  }
  return null;
}

export function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return typeof value === 'string' && value.trim() ? [value.trim()] : [];
  return value.flatMap((item) => {
    if (typeof item === 'string' && item.trim()) return [item.trim()];
    if (item && typeof item === 'object' && 'text' in item && typeof item.text === 'string') return [item.text.trim()];
    return [];
  }).filter(Boolean);
}

export function booleanValue(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

export function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function datetimeValue(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  const text = textValue(value);
  if (!text) return null;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? text : date.toISOString();
}

export function sourceUrl(value: unknown): string | null {
  if (Array.isArray(value)) {
    const linked = value.find((item) => item && typeof item === 'object' && 'link' in item && typeof item.link === 'string');
    if (linked && typeof linked === 'object' && 'link' in linked && typeof linked.link === 'string') return validHttpUrl(linked.link);
  }
  if (value && typeof value === 'object' && 'link' in value && typeof value.link === 'string') return validHttpUrl(value.link);
  const raw = textValue(value);
  if (!raw) return null;
  const markdownMatch = raw.match(/\((https?:\/\/[^)]+)\)/);
  return validHttpUrl(markdownMatch?.[1] ?? raw);
}

function validHttpUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

function sourceTitle(value: unknown): string | null {
  const candidate = Array.isArray(value)
    ? value.find((item) => item && typeof item === 'object' && 'text' in item && typeof item.text === 'string')
    : value;
  if (candidate && typeof candidate === 'object' && 'text' in candidate && typeof candidate.text === 'string') {
    const title = candidate.text.trim();
    if (title && !validHttpUrl(title) && title !== '原文') return title;
  }
  const raw = textValue(value);
  const markdown = raw?.match(/^\[([^\]]+)]\(https?:\/\/[^)]+\)$/);
  const label = markdown?.[1]?.trim();
  return label && !validHttpUrl(label) && label !== '原文' ? label : null;
}

function finalPathName(value: unknown): string | null {
  const path = textValue(value);
  if (!path) return null;
  const segment = path.split(/[\\/]/).filter(Boolean).at(-1)?.trim();
  return segment || null;
}

export function resolveDisplayTitle(fields: Record<string, unknown>): { title: string; source: KnowledgeRecord['titleSource'] } {
  const explicit = textValue(fields['标题/问题']);
  if (explicit) return { title: explicit, source: 'title' };
  const linked = sourceTitle(fields['原文链接']);
  if (linked) return { title: linked, source: 'sourceTitle' };
  const driveFilename = textValue(fields['Drive 文件名']);
  if (driveFilename) return { title: driveFilename, source: 'driveFilename' };
  const sourcePath = finalPathName(fields['来源路径']) ?? finalPathName(fields['目录路径']);
  if (sourcePath) return { title: sourcePath, source: 'sourcePath' };
  return { title: '待补标题', source: 'missing' };
}

export function extractCanonicalToken(url: string | null): { token: string; type: string } | null {
  if (!url) return null;
  const match = url.match(/\/(docx|doc|sheets|base|bitable|file|slides|mindnotes?|wiki)\/([A-Za-z0-9_-]+)/i);
  if (!match) return null;
  const kind = match[1].toLowerCase();
  const typeMap: Record<string, string> = { sheets: 'sheet', base: 'bitable', mindnote: 'mindnote', mindnotes: 'mindnote' };
  return { type: typeMap[kind] ?? kind, token: match[2] };
}

export function normalizeKnowledgeRecord(record: RawRecord): KnowledgeRecord {
  const f = record.fields;
  const displayTitle = resolveDisplayTitle(f);
  return {
    recordId: record.record_id,
    title: displayTitle.title,
    titleSource: displayTitle.source,
    type: textValue(f['类型']),
    project: textValue(f['平台/项目']),
    scope: textValue(f['所属目录']),
    status: textValue(f['整理状态']),
    manualDecision: textValue(f['人工确认']),
    category: textValue(f['一级目录']),
    subcategory: textValue(f['二级目录']),
    topic: textValue(f['三级目录/专题']),
    directoryPath: textValue(f['目录路径']),
    visible: booleanValue(f['目录可见']),
    sourceUrl: sourceUrl(f['原文链接']),
    summary: textValue(f['摘要/结论']),
    batch: textValue(f['录入批次']),
    updatedAt: datetimeValue(f['系统更新时间']),
    raw: f,
  };
}

export function compareKnowledgeRecords(a: KnowledgeRecord, b: KnowledgeRecord): number {
  const aMissing = a.titleSource === 'missing' ? 1 : 0;
  const bMissing = b.titleSource === 'missing' ? 1 : 0;
  if (aMissing !== bMissing) return aMissing - bMissing;
  const aUpdated = a.updatedAt ?? '';
  const bUpdated = b.updatedAt ?? '';
  if (aUpdated !== bUpdated) return aUpdated > bUpdated ? -1 : 1;
  if (a.recordId === b.recordId) return 0;
  return a.recordId < b.recordId ? -1 : 1;
}

export function mergeUniqueRecords(current: KnowledgeRecord[], incoming: KnowledgeRecord[]): KnowledgeRecord[] {
  const records = new Map(current.map((record) => [record.recordId, record]));
  for (const record of incoming) records.set(record.recordId, record);
  return [...records.values()];
}

export function displayCategory(category: string): string {
  return category.replace(/^分类-\d+_/, '').replace(/^\d+_/, '') || category;
}

export function projectPlacement(project: string): { top: string; project: string } {
  if (project.startsWith('平台化专项')) return { top: '平台化与共性', project: '平台化专项' };
  if (project.startsWith('跨平台')) return { top: '平台化与共性', project: '跨平台' };
  if (project === '矩阵级共性' || project === '流程规范') return { top: '平台化与共性', project };

  const platformCommon = project.match(/^([A-Z])平台(?:共性)?$/i);
  if (platformCommon) return { top: `${platformCommon[1].toUpperCase()}平台`, project: '平台共性' };

  const platformProject = project.match(/^([A-Z])平台\/(.+)$/i);
  if (platformProject) {
    const normalized = platformProject[2].replace(/\(ATP\)$/i, '');
    return { top: `${platformProject[1].toUpperCase()}平台`, project: normalized };
  }

  if (/[&|/]/.test(project)) return { top: '跨车型资料', project };
  const normalized = project.replace(/\(ATP\)$/i, '');
  const vehicle = normalized.match(/^([A-Z])\d/i);
  if (vehicle) return { top: `${vehicle[1].toUpperCase()}平台`, project: normalized };
  return { top: '平台化与共性', project: normalized };
}

export function displayProject(project: string | null): string {
  return project ? projectPlacement(project).project : '未标项目';
}

export function isHumanDocument(record: KnowledgeRecord): boolean {
  return record.type === '原文资料' && record.visible !== false;
}

type MutableDirectoryNode = Omit<DirectoryNode, 'children' | 'documentRecordIds' | 'rawValues'> & {
  children: MutableDirectoryNode[];
  projectValues: Set<string>;
  categoryValues: Set<string>;
  scopeValues: Set<string>;
  documentRecordIds: Set<string>;
  childKeys: Map<string, MutableDirectoryNode>;
};

function mutableNode(name: string, path: string, type: string, filters?: DirectoryFilter): MutableDirectoryNode {
  return {
    token: `display:${path || 'root'}`,
    name,
    type,
    path,
    children: [],
    count: 0,
    filters,
    projectValues: new Set(),
    categoryValues: new Set(),
    scopeValues: new Set(),
    documentRecordIds: new Set(),
    childKeys: new Map(),
  };
}

function childOf(parent: MutableDirectoryNode, key: string, name: string, type: string, filters?: DirectoryFilter): MutableDirectoryNode {
  const existing = parent.childKeys.get(key);
  if (existing) return existing;
  const path = `${parent.path}/${encodeURIComponent(key)}`;
  const child = mutableNode(name, path, type, filters);
  parent.childKeys.set(key, child);
  parent.children.push(child);
  return child;
}

function addDocument(node: MutableDirectoryNode, record: KnowledgeRecord): void {
  node.count = (node.count ?? 0) + 1;
  node.documentRecordIds.add(record.recordId);
  if (record.project) node.projectValues.add(record.project);
  if (record.category) node.categoryValues.add(record.category);
  if (record.scope) node.scopeValues.add(record.scope);
}

function sortedNodeNames(a: MutableDirectoryNode, b: MutableDirectoryNode): number {
  const aTop = PLATFORM_ORDER.indexOf(a.name);
  const bTop = PLATFORM_ORDER.indexOf(b.name);
  if (aTop >= 0 || bTop >= 0) return (aTop < 0 ? 999 : aTop) - (bTop < 0 ? 999 : bTop);
  if (a.type === 'project' && b.type === 'project' && (a.name === '平台共性' || b.name === '平台共性')) {
    return a.name === '平台共性' ? 1 : -1;
  }
  const aCategory = CATEGORY_ORDER.get(a.name);
  const bCategory = CATEGORY_ORDER.get(b.name);
  if (aCategory !== undefined || bCategory !== undefined) return (aCategory ?? 999) - (bCategory ?? 999);
  return a.name.localeCompare(b.name, 'zh-CN');
}

function finalizeDirectoryNode(node: MutableDirectoryNode): DirectoryNode {
  node.children.sort(sortedNodeNames);
  const projects = [...node.projectValues].sort((a, b) => a.localeCompare(b, 'zh-CN'));
  const categories = [...node.categoryValues].sort((a, b) => a.localeCompare(b, 'zh-CN'));
  const scopes = [...node.scopeValues].sort((a, b) => a.localeCompare(b, 'zh-CN'));
  const filters = node.filters ?? {
    ...(projects.length ? { projects } : {}),
    ...(categories.length ? { categories } : {}),
  };
  const isCategory = node.type === 'category';
  const target = isCategory && projects.length === 1 && categories.length === 1 && node.scopeValues.size === 1
    ? { scope: [...node.scopeValues][0], project: projects[0], category: categories[0] }
    : undefined;
  return {
    token: node.token,
    name: node.name,
    type: node.type,
    path: node.path,
    count: node.count,
    ...(node.documentRecordIds.size ? { documentRecordIds: [...node.documentRecordIds] } : {}),
    ...(projects.length || categories.length || scopes.length ? { rawValues: { projects, categories, scopes } } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
    ...(target ? { target } : {}),
    children: node.children.map(finalizeDirectoryNode),
  };
}

export function buildDisplayDirectoryTree(records: KnowledgeRecord[]): DirectoryNode {
  const humanDocuments = records.filter(isHumanDocument);
  const root = mutableNode('海外组知识库', '', 'root', {});
  for (const record of humanDocuments) {
    addDocument(root, record);
    if (record.titleSource === 'missing') {
      const pending = childOf(root, 'pending', '待整理', 'group', { needsOrganization: true });
      const missing = childOf(pending, 'missing-title', '待补标题', 'pending', { missingTitle: true });
      addDocument(pending, record);
      addDocument(missing, record);
      continue;
    }
    if (record.visible === false) continue;
    if (!record.project) {
      const pending = childOf(root, 'pending', '待整理', 'group', { needsOrganization: true });
      const missing = childOf(pending, 'missing-project', '未标项目', 'pending', { missingProject: true });
      addDocument(pending, record);
      addDocument(missing, record);
      continue;
    }
    if (!record.category) {
      const pending = childOf(root, 'pending', '待整理', 'group', { needsOrganization: true });
      const missing = childOf(pending, 'missing-category', '未分类', 'pending', { missingCategory: true });
      addDocument(pending, record);
      addDocument(missing, record);
      continue;
    }

    const placement = projectPlacement(record.project);
    const top = childOf(root, `top:${placement.top}`, placement.top, 'platform');
    const project = childOf(top, `project:${placement.project}`, placement.project, 'project');
    const categoryName = displayCategory(record.category);
    const category = childOf(project, `category:${categoryName}`, categoryName, 'category');
    addDocument(top, record);
    addDocument(project, record);
    addDocument(category, record);
  }
  return finalizeDirectoryNode(root);
}

export function normalizeOperationRecord(record: RawRecord): OperationRecord {
  const f = record.fields;
  return {
    recordId: record.record_id,
    cycle: textValue(f['周期']),
    scope: stringList(f['录入范围']),
    newSources: numberValue(f['本周新增原文数']),
    newKnowledge: numberValue(f['本周新增知识点数']),
    pending: numberValue(f['待人工确认数']),
    waitingAccess: numberValue(f['无权限待开权数']),
    longWaitingAccess: numberValue(f['长期待开权数']),
    status: textValue(f['运营状态']),
    conclusion: textValue(f['结论/动作']),
    generatedAt: datetimeValue(f['生成时间']),
  };
}

export function validateSchema(actual: FieldDefinition[]): string[] {
  const byId = new Map(actual.map((field) => [field.id, field]));
  const errors: string[] = [];
  for (const expected of AGENT_FIELDS) {
    const found = byId.get(expected.id);
    if (!found) errors.push(`缺少字段 ${expected.name} (${expected.id})`);
    else if (found.name !== expected.name || found.type !== expected.type) errors.push(`字段映射异常 ${expected.id}: 期望 ${expected.name}/${expected.type}，实际 ${found.name}/${found.type}`);
  }
  for (const name of REQUIRED_AGENT_FIELD_NAMES) {
    if (!actual.some((field) => field.name === name)) errors.push(`缺少关键字段 ${name}`);
  }
  return [...new Set(errors)];
}

export function isPending(record: KnowledgeRecord): boolean {
  return record.status === '需人工确认' && record.manualDecision === null;
}

export function computeOverview(
  records: KnowledgeRecord[],
  mappingErrors: string[] = [],
  refreshedAt = new Date().toISOString(),
): OverviewData {
  const severeMappingFailure = mappingErrors.length >= 3;
  if (severeMappingFailure) {
    return {
      totals: { total: records.length, sources: 0, knowledge: 0, pending: 0, vehicleCount: 0, missingProject: 0, missingCategory: 0, unknownType: records.length, humanDocuments: 0 },
      projects: [], matrix: {}, categories: STANDARD_CATEGORIES, mappingErrors, refreshedAt,
    };
  }

  const projects = new Map<string, { sourceCount: number; knowledgeCount: number; pendingCount: number; updatedAt: string | null; group: '车型/项目' | '平台化/共性' }>();
  const matrix: Record<string, Record<string, number>> = {};
  let sources = 0;
  let knowledge = 0;
  let pending = 0;
  let missingProject = 0;
  let missingCategory = 0;
  let unknownType = 0;
  let humanDocuments = 0;

  for (const record of records) {
    if (record.type === '原文资料') {
      sources += 1;
      if (record.visible !== false && record.project && record.category) {
        humanDocuments += 1;
      }
    }
    else if (record.type === '知识点') knowledge += 1;
    else unknownType += 1;
    if (isPending(record)) pending += 1;
    if (!record.project) missingProject += 1;
    if (!record.category) missingCategory += 1;

    const displayProject = record.project ?? '未标项目';
    const group = COMMON_GROUPS.has(record.scope ?? '') || COMMON_GROUPS.has(displayProject) ? '平台化/共性' : '车型/项目';
    const summary = projects.get(displayProject) ?? { sourceCount: 0, knowledgeCount: 0, pendingCount: 0, updatedAt: null, group };
    if (record.type === '原文资料') summary.sourceCount += 1;
    if (record.type === '知识点') summary.knowledgeCount += 1;
    if (isPending(record)) summary.pendingCount += 1;
    if (record.updatedAt && (!summary.updatedAt || record.updatedAt > summary.updatedAt)) summary.updatedAt = record.updatedAt;
    projects.set(displayProject, summary);

    if (record.type === '原文资料' && record.project && record.category && STANDARD_CATEGORIES.includes(record.category as (typeof STANDARD_CATEGORIES)[number])) {
      matrix[record.project] ??= {};
      matrix[record.project][record.category] = (matrix[record.project][record.category] ?? 0) + 1;
    }
  }

  const projectRows = [...projects.entries()].map(([project, value]) => ({ project, ...value }))
    .sort((a, b) => b.sourceCount + b.knowledgeCount - (a.sourceCount + a.knowledgeCount));
  const vehicleCount = new Set(projectRows.filter((item) => item.group === '车型/项目' && item.project !== '未标项目').map((item) => item.project)).size;

  return {
    totals: { total: records.length, sources, knowledge, pending, vehicleCount, missingProject, missingCategory, unknownType, humanDocuments },
    projects: projectRows,
    matrix,
    categories: STANDARD_CATEGORIES,
    mappingErrors,
    refreshedAt,
  };
}
